import { createSlice, createSelector } from '@reduxjs/toolkit';
import { Constants } from "../../utilities/constants";

const getPreviousDay = (date = new Date()) => {
  const previous = new Date(date.getTime());
  previous.setDate(date.getDate() - 1);

  return previous;
}

export const initialState = {
  dashboardSelectedTab: 'workload',
  dashboardTasks: [],
  dashboardAvailableTasks: [],
  myTasksSelectedUsers: [],
  activeWorkflowTasks: [],
  dashboardChannels: [
    Constants.Channel.MLS,
  ],
  terminatedTasks: 0,
  closedTasks: 0,
  filterEditorOpen: false,
  filter: null,
  rules: null,
  myTasksIsLoading: true,
  activeWorkflowIsLoading: true,
  completedTasksIsLoading: true,
  savedFilters: [],
  dashboardRegions: [],
  dashboardStrategies: [],
  rentalInventory: {
    inventoryData: [],
    isLoading: true,
    fromDate: new Date(getPreviousDay().toLocaleDateString()),
    toDate: new Date((new Date()).toLocaleDateString()),
    fromTime: new Date(getPreviousDay().toLocaleDateString() + " 17:00:00"),
    toTime: new Date((new Date()).toLocaleDateString() + " 16:59:59"),
    selectedStrategies: [],
    strategiesOpen: false,
    allStrategiesIncluded: 'includeAll',
    tbbpThreshold: 65,
    dashboardMaxPrices: {},
  },
  retailInventory: {
    inventoryData: [],
    isLoading: true,
    fromDate: new Date(getPreviousDay().toLocaleDateString()),
    toDate: new Date((new Date()).toLocaleDateString()),
    fromTime: new Date(getPreviousDay().toLocaleDateString() + " 17:00:00"),
    toTime: new Date((new Date()).toLocaleDateString() + " 16:59:59"),
    selectedStrategies: [],
    strategiesOpen: false,
    allStrategiesIncluded: 'includeAll',
    tbbpThreshold: 65,
    dashboardMaxPrices: {},
  }
}

const getType = (inventoryType) => {
  return inventoryType.toLowerCase() === 'rental' ? 'rentalInventory' : 'retailInventory';
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'dashboardSlice',
  initialState,
  reducers: {
    setSelectedTab: (state, action) => {
      state.dashboardSelectedTab = action.payload;
    },
    toggleDashboardFilterEditor: (state, action) => {
      state.filterEditorOpen = action.payload;
    },
    setDashboardFilter: (state, action) => {
      state.filter = action.payload;
    },
    setDashboardRules: (state, action) => {
      state.rules = action.payload;
    },
    setDashboardChannels: (state, action) => {
      state.dashboardChannels = action.payload;
    },
    setRegions: (state, action) => {
      state.dashboardRegions = action.payload.regions;
    },
    setStrategies: (state, action) => {
      state.dashboardStrategies = action.payload.strategies;
    },
    setDashboardTasks: (state, action) => {
      state.dashboardTasks = action.payload.dashboardTasks || [];
    },
    setDashboardAvailableTasks: (state, action) => {
      state.dashboardAvailableTasks = action.payload.dashboardAvailableTasks || [];
    },
    setDashboardActiveWorkflowTasks: (state, action) => {
      state.activeWorkflowTasks = action.payload.dashboardActiveWorkflowTasks || [];
    },
    setDashboardTerminatedTasks: (state, action) => {
      console.log(action.payload);
      state.terminatedTasks = action.payload;
    },
    setDashboardClosedTasks: (state, action) => {
      state.closedTasks = action.payload;
    },
    setMyTasksSelectedUsers: (state, action) => {
      state.myTasksSelectedUsers = action.payload;
    },
    setMyTasksIsLoading: (state, action) => {
      state.myTasksIsLoading = action.payload;
    },
    setAvailableTasksIsLoading: (state, action) => {
      state.availableIsLoading = action.payload;
    },
    setActiveWorkflowIsLoading: (state, action) => {
      state.activeWorkflowIsLoading = action.payload;
    },
    setCompletedTasksIsLoading: (state, action) => {
      state.completedTasksIsLoading = action.payload;
    },
    setSavedFilters: (state, action) => {
      state.savedFilters = action.payload;
    },

    // inventories
    setDashboardMaxPriceByRegion: (state, action) => {
      const type = getType(action.payload.inventoryType);
      state[type].dashboardMaxPrices[action.payload.region] = action.payload.maxPrice;
    },
    setInventoryData: (state, action) => {
      const type = getType(action.payload.inventoryType);
      state[type].inventoryData = action.payload.inventoryData;
    },
    setIsLoading: (state, action) => {
      const type = getType(action.payload.inventoryType)
      state[type].isLoading = action.payload.isLoading;
    },
    setFromTime: (state, action) => { 
      const type = getType(action.payload.inventoryType)
      state[type].fromTime = action.payload.date;
    },
    setToTime: (state, action) => {
      const type = getType(action.payload.inventoryType)
      state[type].toTime = action.payload.date;
    },
    setSelectedStrategies: (state, action) => {
      const type = getType(action.payload.inventoryType)
      state[type].selectedStrategies = action.payload.strategies;
    },
    setStrategiesOpen: (state, action) => {
      const type = getType(action.payload.inventoryType)
      state[type].strategiesOpen = action.payload.state;
    },
    setAllStrategiesIncluded: (state, action) => {
      const type = getType(action.payload.inventoryType)
      state[type].allStrategiesIncluded = action.payload.state;
    },
    setTbbpThreshold: (state, action) => {
      const type = getType(action.payload.inventoryType)
      state[type].tbbpThreshold = action.payload.level;
    },

    // Redux saga actions, no reducers
    fetchDashboardTasks: { // Triggers getDashboardTasksSaga
      reducer: () => { }, 
      prepare: (channels, selectedUsers) => ({ payload: { channels, selectedUsers } })
    },
    fetchDashboardAvailableTasks: () => { }, // Triggers getDashboardActiveTasksSaga
    fetchDashboardActiveWorkflowTasks: () => { }, // Triggers getDashboardActiveWorkflowTasksSaga
    fetchDashboardTaskCountByStatus: () => { }, // Triggers getDashboardTaskCountByStatusSaga
    fetchInventoryData: () => { }, // Triggers getDashboardInventoryDataSaga
    fetchRegions: () => {}, // Triggers getDashboardInventoryRegionsSaga
    fetchStrategies: () => {}, // Triggers getDashboardStrategiesSaga
  }
});

export default slice.reducer;

export const dashboardActions = slice.actions;

// Memoized selectors
export const dashboardSelectors = {
  rentalInventorySelector: createSelector(
    state => state.dashboardState.rentalInventory,
    rentalInventory => rentalInventory
  ),
  retailInventorySelector: createSelector(
    state => state.dashboardState.retailInventory,
    retailInventory => retailInventory
  ),
  dashboardChannelsSelector: createSelector(
    state => state.dashboardState.dashboardChannels,
    dashboardChannels => dashboardChannels
  ),
  dashboardFilterSelector: createSelector(
    state => state.dashboardState.filter,
    filter => filter
  ), 
}